import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Eye, MessageCircle, Share2, TrendingUp, RefreshCw } from 'lucide-react';
import { Table, Button, message, Select } from 'antd';
import api from '../api/client';
import dayjs from 'dayjs';

const { Option } = Select;

export default function Content() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [channels, setChannels] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState(null);

  useEffect(() => {
    fetchChannels();
  }, []);

  useEffect(() => {
    if (selectedChannel) {
      fetchPosts(selectedChannel);
    }
  }, [selectedChannel]);

  const fetchChannels = async () => {
    try {
      const res = await api.get('/channels');
      setChannels(res.data);
      if (res.data.length > 0) {
        setSelectedChannel(res.data[0].id);
      } else {
        setLoading(false);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchPosts = async (channelId) => {
    try {
      setLoading(true);
      const res = await api.get('/posts', { params: { channel_id: channelId } });
      setPosts(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      await fetchPosts(selectedChannel);
      message.info('История постов не подтягивается через Bot API. Статистика собирается для новых постов автоматически.');
    } catch (err) {
      message.error('Ошибка обновления');
    } finally {
      setRefreshing(false);
    }
  };

  const columns = [
    {
      title: 'Пост',
      dataIndex: 'content_preview',
      key: 'content',
      width: '40%',
      render: (text) => (
        <div className="line-clamp-2 text-slate-200">{text || 'Медиа контент'}</div>
      ),
    },
    {
      title: 'Дата',
      dataIndex: 'posted_at',
      key: 'date',
      render: (date) => <span className="text-slate-400">{dayjs(date).format('DD.MM HH:mm')}</span>,
    },
    {
      title: 'Просмотры',
      dataIndex: 'views',
      key: 'views',
      render: (val) => (
        <div className="flex items-center gap-2 text-slate-300">
          <Eye size={16} /> {val}
        </div>
      ),
      sorter: (a, b) => a.views - b.views,
    },
    {
      title: 'Реакции',
      dataIndex: 'reactions',
      key: 'reactions',
      render: (val) => (
        <div className="flex items-center gap-2 text-rose-400">
          <MessageCircle size={16} /> {val}
        </div>
      ),
    },
    {
      title: 'ER',
      key: 'er',
      render: (_, record) => {
        const er = record.views > 0 ? ((record.reactions + record.forwards) / record.views * 100).toFixed(1) : 0;
        return (
          <div className="flex items-center gap-2 text-emerald-400 font-medium">
            <TrendingUp size={16} /> {er}%
          </div>
        );
      }
    }
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white mb-2">Контент</h1>
          <p className="text-slate-400">Аналитика эффективности публикаций</p>
        </div>
        <div className="flex items-center gap-3">
          <Select
            value={selectedChannel}
            onChange={setSelectedChannel}
            className="w-64"
            dropdownStyle={{ backgroundColor: '#1e293b', borderColor: '#334155' }}
          >
            {channels.map(ch => (
              <Option key={ch.id} value={ch.id}>{ch.channel_name || ch.channel_username}</Option>
            ))}
          </Select>
          <Button 
            icon={<RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />} 
            onClick={handleRefresh}
            disabled={!selectedChannel}
            className="bg-primary/10 text-primary border-primary/20 hover:bg-primary/20 disabled:opacity-50"
          >
            Обновить
          </Button>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-surface rounded-xl border border-white/5 overflow-hidden"
      >
        <Table
          dataSource={posts}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          className="custom-table"
          locale={{ emptyText: <div className="py-8 text-slate-500">Нет данных о постах. Нажмите "Обновить".</div> }}
        />
      </motion.div>
    </div>
  );
}
